import { normalizeLanguage } from "./languages";

/**
 * localStorage key holding the user's chosen language.
 *
 * The value is stored **JSON-quoted** (`"de"`, not `de`). That is not a
 * decision so much as an inheritance: the original picker was a
 * `useStickyState<string>("coffee-language", "en")`, and `useStickyState`
 * `JSON.stringify`s everything it writes. Anything still reading this key
 * through `useStickyState` would get `undefined` from a bare `de`, so we keep
 * writing the quoted form. `normalizeLanguage` strips the quotes on read and so
 * tolerates both shapes, which is also what lets the i18next localStorage
 * detector read the key (see `convertDetectedLanguage` in `./index.ts`).
 */
export const LANGUAGE_STORAGE_KEY = "coffee-language";

/**
 * Where the value under `LANGUAGE_STORAGE_KEY` came from.
 *
 * That key is a *cache of the effective language*, not a record of a choice: it
 * holds the user's own language when they have one, and the workspace default
 * when they don't. The two have to be told apart for one case — a workspace
 * default an admin later clears has to stop applying, whereas a personal choice
 * never does. Without the provenance the cleared default would linger in
 * storage forever with nothing left to correct it.
 *
 * Absent for anyone whose cache predates this key, which reads as "user": the
 * cautious guess, since it only ever prevents a clear.
 */
export const LANGUAGE_SOURCE_STORAGE_KEY = "coffee-language-source";

export type StoredLanguageSource = "user" | "workspace";

/**
 * The stored language, or null when nothing valid is stored. Never throws:
 * Safari private mode and storage-blocking extensions make `localStorage`
 * access itself a throwing operation.
 */
export function readStoredLanguage(): string | null {
  if (typeof window === "undefined") return null; // SSR guard
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Cache the effective language for the next boot. Silently no-ops when storage
 * is unavailable.
 */
export function writeStoredLanguage(code: string, source: StoredLanguageSource = "user"): void {
  if (typeof window === "undefined") return; // SSR guard
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify(code));
    window.localStorage.setItem(LANGUAGE_SOURCE_STORAGE_KEY, source);
  } catch {
    // Quota exceeded / private browsing — the language still applies for this
    // session, it just won't survive a reload.
  }
}

/** Provenance of the cached language. Unknown or unreadable reads as "user". */
export function readStoredLanguageSource(): StoredLanguageSource {
  if (typeof window === "undefined") return "user"; // SSR guard
  try {
    return window.localStorage.getItem(LANGUAGE_SOURCE_STORAGE_KEY) === "workspace"
      ? "workspace"
      : "user";
  } catch {
    return "user";
  }
}

/** Drop the cached language, so the next detection starts from the browser. */
export function clearStoredLanguage(): void {
  if (typeof window === "undefined") return; // SSR guard
  try {
    window.localStorage.removeItem(LANGUAGE_STORAGE_KEY);
    window.localStorage.removeItem(LANGUAGE_SOURCE_STORAGE_KEY);
  } catch {
    // Nothing to do — an unreadable store is also an unwritable one.
  }
}
